import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '@aprova-digital/design-system';

import { Types } from '../../../domain/types.domain';
import { GovBrCompany } from '../../../domain/auth/dtos/start-account-creation.output';
import {
  IconComponent,
  LoadingBarComponent,
  StepConfig,
  StepperComponent,
} from '../../../@ads/components';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { UserRepositoryInstruction } from '../../../builder/instructions/repositories/user.repository.instructions';

import { AppHandler } from '../../app.handler';
import { PageTitleComponent } from '../../components/abstracts/page-title.component';
import { AccountFormHandler } from '../create-account/account-form.handler';
import { AddressFormComponent } from '../create-account/components/address-form/address-form.component';
import { CreateAccountPjFormHandler } from '../create-account-pj/create-account-pj-form.handler';
import { CompanyFormComponent } from '../create-account-pj/components/company-form/company-form.component';
import { AccountOptionsState } from '../account-options/account-options.component';

export interface CompanyRegistrationState {
  company: GovBrCompany;
  contextId: string;
  origin: AccountOptionsState;
}

@Component({
  selector: 'app-company-registration',
  standalone: true,
  imports: [
    ButtonComponent,
    IconComponent,
    LoadingBarComponent,
    StepperComponent,
    CompanyFormComponent,
    AddressFormComponent,
  ],
  providers: [
    CreateAccountPjFormHandler,
    AccountFormHandler,
    new AuthRepositoryInstruction(),
    new UserRepositoryInstruction(),
  ],
  templateUrl: './company-registration.component.html',
  styleUrl: '../create-account-pj/create-account-pj.component.scss',
})
export class CompanyRegistrationComponent
  extends PageTitleComponent('Criar conta PJ')
  implements OnInit
{
  @ViewChild(CompanyFormComponent)
  private _companyFormRef: CompanyFormComponent | undefined;

  @ViewChild(AddressFormComponent)
  private _addressFormRef: AddressFormComponent | undefined;

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appHandler = inject(AppHandler);
  private readonly _handler = inject(CreateAccountPjFormHandler);
  private readonly _accountFormHandler = inject(AccountFormHandler);
  private readonly _authRepository = inject(Types.AuthRepository);

  private readonly _state = this._router.getCurrentNavigation()?.extras?.state as
    | CompanyRegistrationState
    | undefined;

  public readonly steps: StepConfig[] = [
    { id: 'dados', title: 'Dados', enabled: true },
    { id: 'endereco', title: 'Endereço', enabled: true },
  ];

  public currentStep = signal(0);
  public stepValidity = signal([false, false]);
  public stepping = signal(false);
  public saving = signal(false);
  public createdEmail = signal<string | null>(null);

  get city() {
    return this._route.snapshot.data['city'];
  }

  ngOnInit(): void {
    this._appHandler.setLoading(false);
    this._appHandler.hideBackButton(true);

    if (!this._state) {
      this.redirectToLogin();
      return;
    }

    this._handler.setCompanyInfo({
      razaoSocial: this._state.company.razaoSocial,
      cnpj: this._state.company.cnpj,
      nomeFantasia: '',
      telefone: '',
      email: '',
      emailConfirmacao: '',
    });
  }

  setStepValidation(step: number, valid: boolean): void {
    this.stepValidity.update((validity) => validity.map((value, index) => (index === step ? valid : value)));
  }

  goBack(): void {
    if (this.currentStep() === 0) {
      this._router.navigate([`/${this.city?.identidade}/account-options`], { state: this._state!.origin });
      return;
    }
    this.currentStep.set(0);
  }

  async goToNextStep(): Promise<void> {
    if (this.stepping()) return;

    if (this.currentStep() === 0) {
      this.stepping.set(true);
      try {
        if (await this._companyFormRef?.validateEmailAndCnpj()) {
          this.currentStep.set(1);
        }
      } finally {
        this.stepping.set(false);
      }
      return;
    }

    await this.createAccount();
  }

  redirectToLogin(): void {
    if (this.city?.loginUrl) {
      window.location.href = this.city.loginUrl;
      return;
    }

    this._router.navigate([`/${this.city?.identidade}`]);
  }

  private async createAccount(): Promise<void> {
    const company = this._handler.getCompanyInfo()!;
    const address = this._accountFormHandler.getAddressInfo() ?? {};

    this.saving.set(true);
    try {
      const { email } = await this._authRepository.createCompanyAccount({
        contextId: this._state!.contextId,
        cnpj: this._state!.company.cnpj,
        connection: this.city.connection,
        termsAccepted: this._addressFormRef?.form.controls.termos.value === true,
        company: {
          nomeFantasia: company.nomeFantasia || undefined,
          telefone: company.telefone,
          email: company.email,
        },
        address: {
          cep: address.cep ?? '',
          rua: address.rua ?? '',
          numero: address.numero || 'S/N',
          complemento: address.complemento || undefined,
          bairro: address.bairro ?? '',
          cidade: address.cidade ?? '',
          estado: address.estado ?? '',
        },
      });
      this.createdEmail.set(email);
    } catch (errorResponse: any) {
      const code = errorResponse?.error?.code;
      const mustRestart = code === 'accountCreationExpired' || code === 'companyNotVerified';
      this._appHandler.setError(true, {
        message: 'Não foi possível criar a conta',
        description: mustRestart
          ? 'Entre com o gov.br novamente para continuar o cadastro.'
          : errorResponse?.error?.errorMessages?.[0] ??
            errorResponse?.error?.message ??
            'Se o problema persistir, entre em contato com nossa equipe de suporte.',
        actionText: mustRestart ? 'Voltar ao login' : 'Tentar novamente',
        action: () => {
          this._appHandler.clearError();
          if (mustRestart) this.redirectToLogin();
        },
      });
    } finally {
      this.saving.set(false);
    }
  }
}
